// shared/names.js — first-name-unless-clash display names for active company
// members (D11, docs/multi-user-roles-spec.md §5.5).
//
// Lives in shared/ so the server and the browser call one implementation. Pure:
// no imports, no DOM, no node builtins.
export function displayNamesFor(members) {
  const firstNameOf = (member) => {
    const trimmed = String(member?.name || "").trim();
    if (trimmed) return trimmed.split(/\s+/)[0];
    // No name on file (optional at registration) — fall back to email. Email
    // is globally unique, so two members can never clash on this fallback.
    return String(member?.email || "").trim() || "Member";
  };
  const fullNameOf = (member) => {
    const trimmed = String(member?.name || "").trim();
    return trimmed || String(member?.email || "").trim() || "Member";
  };

  // Clash detection is case-insensitive ("Jon" and "jon" are the same first
  // name for this purpose) — but the *display* keeps each member's own
  // casing, so the key used for counting is lower-cased while the value
  // stored per-member is not.
  const firstNameCounts = new Map();
  for (const member of members) {
    const key = firstNameOf(member).toLowerCase();
    firstNameCounts.set(key, (firstNameCounts.get(key) || 0) + 1);
  }

  const displayNames = new Map();
  for (const member of members) {
    const first = firstNameOf(member);
    const clashes = (firstNameCounts.get(first.toLowerCase()) || 0) > 1;
    displayNames.set(member.id, clashes ? fullNameOf(member) : first);
  }
  return displayNames;
}

/**
 * A person's initials, for a visit on the Schedule that isn't yours
 * (Jobs and Schedule v1, stage 5): the first letters of their first and last
 * names, "Sam Lee" → "SL"; one name → its first letter; no name → the first
 * letter of their email. Upper case, at most two letters.
 */
export function initialsFor(member) {
  const words = String(member?.name || "").trim().split(/\s+/).filter(Boolean);
  const letter = (word) => [...String(word || "")].find((ch) => /\p{L}|\p{N}/u.test(ch)) || "";
  let out;
  if (words.length >= 2) out = letter(words[0]) + letter(words.at(-1));
  else if (words.length === 1) out = letter(words[0]);
  else out = letter(String(member?.email || "").trim());
  return (out || "?").toUpperCase();
}
